import { useContext, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router';
import * as yup from 'yup';
import { Button, Stack } from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  useCreateEnvelope,
  useListSignatureServices,
} from '~/core/shared/api/signature';
import {
  Dialog,
  DialogProps,
  FormField,
  Input,
  Select,
} from '~/core/shared/components';
import { EMPTY_ARR } from '~/core/shared/constants';
import { useFormResolver } from '~/core/shared/hooks';
import { Envelope, SignatureService } from '~/core/shared/types';
import { ApplicationContext } from '../Layout.component';

interface ApplicationEnvelopesCreateModalProps
  extends Omit<DialogProps, 'children'> {
  modalApi: UseOpenCloseReturn;
  editPath: string;
}
const validationSchema = yup.object({
  name: yup.string().trim().required(),
  service_identifier: yup.string().trim().required(),
});

export const ApplicationEnvelopesCreateModal = (
  props: ApplicationEnvelopesCreateModalProps,
) => {
  const { createEnvelope } = useCreateEnvelope();
  const { editPath, modalApi, ...modalProps } = props;

  const { applicationUuid } = useContext(ApplicationContext);
  // TODO: @haxxxton update this to use form blocks
  const resolver = useFormResolver<Partial<Envelope>>(validationSchema);
  const form = useForm<Partial<Envelope>>({ resolver });
  const navigate = useNavigate();

  const { services } = useListSignatureServices(undefined, {
    enabled: modalApi.open,
  });

  const serviceOptions = useMemo(
    () =>
      (services ?? (EMPTY_ARR as SignatureService[])).map((service) => ({
        label: service.name,
        value: service.identifier,
      })),
    [services],
  );

  const onSubmit = async (data: Partial<Envelope>) => {
    try {
      const envelope = await createEnvelope({
        document_id: applicationUuid,
        name: data.name,
        service_identifier: data.service_identifier!,
      });

      if (envelope) {
        navigate(
          generatePath(editPath, {
            envelope: envelope.id,
            uuid: applicationUuid,
          }),
        );
      }
    } catch {
      // error is handled in useCreateEnvelope
    }
  };

  return (
    <Dialog
      header="Start envelope"
      size="lg"
      {...modalProps}
      onOpenChange={modalApi.onOpenChange}
      open={modalApi.open}
    >
      <Stack gap={8}>
        <FormProvider {...form}>
          <Stack as="form" gap={4} onSubmit={form.handleSubmit(onSubmit)}>
            <FormField component={Input} label="Name" name="name" required />
            <FormField
              component={Select}
              hidePlaceholder
              name="service_identifier"
              options={serviceOptions}
              required
            />
            <Button colorPalette="primary" type="submit" variant="solid">
              Create & View
            </Button>
          </Stack>
        </FormProvider>
      </Stack>
    </Dialog>
  );
};
